import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from './Modal';
import {
  getReportOptions,
  submitReport,
  type ReportOption,
  type ReportTargetType,
} from '../../services/reportApi';
import './ReportContentModal.css';

interface ReportContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetType: ReportTargetType;
  targetId: string;
  /** What is being reported, in the user's words, for the modal's own copy. */
  targetLabel?: string;
  /** Where the reporter was. Recorded as context, never as a target. */
  context?: string;
  /**
   * What the reporter saw. Required for targets the server cannot resolve —
   * an AI turn's id is minted client-side and regenerated on rehydration, so
   * without this the report points at nothing an operator can find.
   */
  contentSnapshot?: string;
}

/**
 * The report dialog behind every "Report" control.
 *
 * The reason list comes from the server rather than a constant here, so the
 * client cannot offer a reason the API will reject — the two vocabularies are
 * one vocabulary.
 *
 * There is no signed-in check. Reporting has to work for the people browsing
 * Explore without an account, which is most of the people who will meet
 * something worth reporting.
 */
export const ReportContentModal: React.FC<ReportContentModalProps> = ({
  isOpen,
  onClose,
  targetType,
  targetId,
  targetLabel,
  context,
  contentSnapshot,
}) => {
  const [reasons, setReasons] = useState<ReportOption[]>([]);
  const [maxDetail, setMaxDetail] = useState(1000);
  const [reason, setReason] = useState('');
  const [detail, setDetail] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    let live = true;
    getReportOptions()
      .then((options) => {
        if (!live) return;
        setReasons(options.reasons);
        setMaxDetail(options.max_detail_chars);
      })
      .catch(() => {
        // A failed vocabulary fetch must not leave a dead dialog. The reasons
        // below are the server's own list; if it has changed, the submit call
        // is the thing that will say so.
        if (!live) return;
        setReasons([
          { value: 'offensive', label: 'Offensive or hateful' },
          { value: 'harmful', label: 'Dangerous or unsafe advice' },
          { value: 'sexual', label: 'Sexual content' },
          { value: 'inaccurate', label: 'Wrong or misleading' },
          { value: 'spam', label: 'Spam or advertising' },
          { value: 'other', label: 'Something else' },
        ]);
      });
    return () => {
      live = false;
    };
  }, [isOpen]);

  // Reopening after a send should offer a fresh form, not the last one's
  // thank-you note.
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setDetail('');
      setState('idle');
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = useCallback(async () => {
    if (!reason) return;
    setState('sending');
    setError('');
    try {
      await submitReport({
        target_type: targetType,
        target_id: targetId,
        reason,
        detail: detail.trim(),
        context,
        content_snapshot: contentSnapshot,
      });
      setState('sent');
    } catch (err) {
      setState('error');
      // Branch on the status field, not the message: `ApiError` carries the
      // code in `status` and puts DRF's textual detail in `message`, so a
      // string match on "429" never fired and a throttled reporter was told
      // their connection had failed.
      const status = (err as { status?: number } | null)?.status;
      setError(
        status === 429
          ? 'You have sent several reports recently. Try again a little later.'
          : 'That did not send. Check your connection and try again.',
      );
    }
  }, [reason, detail, targetType, targetId, context, contentSnapshot]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Report this" size="sm">
      {state === 'sent' ? (
        <div className="report-modal">
          <p className="report-modal__thanks">Thank you — this has been sent for review.</p>
          <p className="report-modal__note">
            We read every report. If something needs to come down, it comes down.
          </p>
          <div className="report-modal__actions">
            <button type="button" className="report-modal__submit" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      ) : (
        <div className="report-modal">
          {targetLabel && (
            <p className="report-modal__target">
              Reporting <strong>{targetLabel}</strong>
            </p>
          )}

          <fieldset className="report-modal__reasons">
            <legend className="report-modal__legend">What is wrong with it?</legend>
            {reasons.map((option) => (
              <label key={option.value} className="report-modal__reason">
                <input
                  type="radio"
                  name="report-reason"
                  value={option.value}
                  checked={reason === option.value}
                  onChange={() => setReason(option.value)}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </fieldset>

          <label className="report-modal__detail-label" htmlFor="report-detail">
            Anything else we should know? <span>(optional)</span>
          </label>
          <textarea
            id="report-detail"
            className="report-modal__detail"
            value={detail}
            maxLength={maxDetail}
            rows={3}
            onChange={(e) => setDetail(e.target.value)}
            placeholder="Tell us what you saw"
          />

          {error && <p className="report-modal__error">{error}</p>}

          <div className="report-modal__actions">
            <button type="button" className="report-modal__cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="report-modal__submit"
              onClick={handleSubmit}
              disabled={!reason || state === 'sending'}
            >
              {state === 'sending' ? 'Sending…' : 'Send report'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};
